import React, { useState, useContext, useEffect, useCallback, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Container, Grid, Paper } from '@material-ui/core'
import { LockRounded as LockIcon } from '@material-ui/icons'

import StoreContext from '../../store/Context'
import api from '../../services/api'
import messages from '../../services/messages'
import { Link as RouterLink } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setAvailabilities } from '../../redux/availabilities/actions'
import { setFeatures } from '../../redux/features/actions'
import { setCompany } from '../../redux/company/actions'
import { setPermissions } from '../../redux/permissions/actions'
import { setProfiles } from '../../redux/profiles/actions'
import { setRoles } from '../../redux/roles/actions'
import { setUser } from '../../redux/user/actions'
import TextField from '../../components/atoms/inputs/textfield'
import CheckBox from '../../components/atoms/inputs/checkbox'
import Button from '../../components/atoms/inputs/button'
import Typography from '../../components/atoms/display/typography'
import CircularProgress from '../../components/atoms/feedback/circularProgress'
import Snackbar from '../../components/atoms/feedback/snackbar'
import SwitchLanguage from '../../components/molecules/switchs/language'
import SwitchTheme from '../../components/molecules/switchs/theme'
import { setPermissionsExtra } from '../../redux/permissions_extra/actions'
import { useSnackbar } from 'notistack'
import ReCAPTCHA from "react-google-recaptcha";

const Signin = () => {
  const recaptchaRef = useRef(null);

  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const params = new URLSearchParams(useLocation().search)
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({})
  const [formError, setFormError] = useState({})
  const [snackbar, setSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const { setToken } = useContext(StoreContext)
  const history = useHistory()
  const { closeSnackbar } = useSnackbar()
  const version = useSelector((state) => state.version)

  useEffect(() => {
    if (localStorage.getItem('rememberMe')) {
      setForm((form) => ({
        ...form,
        email: localStorage.getItem('rememberMe'),
        rememberMe: 'checked',
      }))
    }
  }, [])

  const inputHandle = (event) => {
    event.persist()
    setForm((form) => ({
      ...form,
      [event?.target?.name]: event?.target?.value,
    }))
  }

  const handleRememberMeChange = (event) => {
    event.persist()
    setForm((form) => ({ ...form, rememberMe: event?.target?.checked }))
  }

  const submit = async (tokenRecaptcha) => {
    setFormError({})
    api({
      method: 'POST',
      url: '/login',
      data: {
        email: form.email,
        password: form.password,
        slug:
          process.env.REACT_APP_NODE_ENV === 'PROD'
            ? process.env.REACT_APP_SLUG
            : params.get('slug') || process.env.REACT_APP_SLUG,
        recaptcha_token: tokenRecaptcha,
      },
    })
      .then((response) => {
        if(response.data.data.token){
          const token = response.data.data.token
          history.push({
            pathname: '/expire-password',
            state: { token: token },
          })
          return
        }
        setLoading(false)
        let data = response.data.data
        api.defaults.headers.common['Authorization'] = data.Authorization
        messages.defaults.headers.common['Authorization'] = data.Authorization
        setToken(data.Authorization)
        dispatch(setAvailabilities(data.availabilities))
        dispatch(setFeatures(data.features))
        dispatch(setCompany(data.company))
        dispatch(setFeatures(data.features))
        dispatch(setPermissions(data.permissions))
        dispatch(setProfiles(data.profiles))
        dispatch(setRoles(data.roles))
        dispatch(
          setUser({
            ...data.user,
            Authorization: data.Authorization,
            AuthorizationRA: data.AuthorizationRA,
          })
        )
        closeSnackbar()
        dispatch(setPermissionsExtra(data?.permissions_extra))
        if (form.rememberMe) {
          localStorage.setItem('rememberMe', form.email)
        } else {
          localStorage.removeItem('rememberMe')
        }
        return history.push('/dashboard')
      })
      .catch((error) => {
        setLoading(false)
        let data = error?.response?.data
        Array.isArray(data?.message) &&
          data?.message?.map((el) => {
            if (el.key === 'email/password') {
              setSnackbarMessage(t(`commons.errors.${el.value}`))
              setSnackbar(true)
              return false
            } else {
              enqueueSnackbar(t(`commons.errors.${el.value}`), {
                variant: 'error',
              })
              return setFormError((formError) => ({
                ...formError,
                [el.key]: el.value,
              }))
            }
          })
      })
  }

  const submitCallback = async () => {
    setLoading(true);
    if(recaptchaRef.current.getValue()) recaptchaRef.current.reset();
    await recaptchaRef.current.executeAsync().then(async (token) =>{
      if(token)     {
        await submit(token);
      }      
    })
  }

  return (
    <>
      <Container>
        <div style={{ height: 84 }} />
        <Grid container direction="row" spacing={2} justify="center">
          <Grid item xl={4} lg={4} md={6} sm={8} xs={12}>
            <Paper elevation={12}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 120,
                  backgroundColor: '#f6f6f6',
                }}
              >
                <img src="/logo.png" alt="Porto" width="180" height="56" />
              </div>
              <div style={{ padding: 50 }}>
                <Grid container direction="row" spacing={2}>
                  <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                    <TextField
                      id="field-email"
                      label={t('fields.email.label')}
                      placeholder={t('fields.email.placeHolder')}
                      type="email"
                      color="primary"
                      variant="outlined"
                      name="email"
                      value={form.email ? form.email : ''}
                      onChange={inputHandle}
                      error={formError?.email ? true : false}
                      helperText={
                        formError?.email &&
                        t(`fields.email.errors.${formError.email}`)
                      }
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter') {
                          submitCallback()
                        }
                      }}
                      autoComplete="off"
                      shrink={true}
                      fullWidth={true}
                    />
                  </Grid>
                  <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                    <TextField
                      id="field-password"
                      label={t('fields.password.label')}
                      placeholder={t('fields.password.placeHolder')}
                      type="password"
                      color="primary"
                      variant="outlined"
                      name="password"
                      value={form.password ? form.password : ''}
                      onChange={inputHandle}
                      error={formError?.password ? true : false}
                      helperText={
                        formError?.password &&
                        t(`fields.password.errors.${formError.password}`)
                      }
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter') {
                          submitCallback()
                        }
                      }}
                      autoComplete="off"
                      shrink={true}
                      fullWidth={true}
                    />
                  </Grid>
                  <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                    <CheckBox
                      checked={form.rememberMe ? form.rememberMe : false}
                      onChange={handleRememberMeChange}
                      name="rememberMe"
                      color="primary"
                      label={t('fields.rememberMe.label')}
                    />
                  </Grid>
                  <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                    <Button
                      size="large"
                      variant="contained"
                      color="primary"
                      fullWidth={true}
                      onClick={() => submitCallback()}
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={25} color="inherit" />
                      ) : (
                        t('buttons.accessService')
                      )}
                    </Button>
                  </Grid>
                  <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                    <Button
                      size="large"
                      color="primary"
                      fullWidth={true}
                      startIcon={<LockIcon color="primary" />}
                      component={RouterLink}
                      to="/password-recover"
                    >
                      {t('buttons.forgotYourPassword')}
                    </Button>
                  </Grid>

                </Grid>
              </div>
            </Paper>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingTop: 25,
              }}
            >
              <Typography variant="body1">
                © {new Date().getFullYear()} {t('app.copyright')}
              </Typography>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingTop: 5,
              }}
            >
              <SwitchLanguage />
              <div style={{ width: 10 }} />
              <SwitchTheme />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingTop: 5,
              }}
            >
              <Typography variant="spam">v {version}</Typography>
            </div>
          </Grid>
        </Grid>
      </Container>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbar}
        onClose={() => setSnackbar(false)}
        autoHideDuration={3000}
        message={snackbarMessage}
      />
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}
        size="invisible"
        badge="bottomright"
      />
    </>
  )
}

export default Signin
